import { useEffect, useState } from "react";
import { PayModeBadge } from "../lib/payMode";
import { Link } from "react-router-dom";
import { MaterialConsumptionReport } from "../components/MaterialConsumptionReport";
import api, { API_BASE } from "../lib/api";
import { Download, BarChart3 } from "lucide-react";
import { AccountantPackage } from "../components/AccountantPackage";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

export default function Reports() {
    const [year, setYear] = useState(CURRENT_YEAR);
    const [month, setMonth] = useState("");
    const [data, setData] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams();
        if (year) params.set("year", year);
        if (month) params.set("month", month);
        api.get(`/reports/summary?${params}`).then((r) => setData(r.data));
    }, [year, month]);

    const exportFile = (format) => {
        const params = new URLSearchParams({ format });
        if (year) params.set("year", year);
        if (month) params.set("month", month);
        window.open(`${API_BASE}/reports/export?${params}`, "_blank");
    };

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="reports-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Jednatel</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Firemní report</h1>
                    <p className="text-sm text-slate-600 mt-2">Obrat, zisk po zakázkách, pohledávky — jedním klikem do PDF nebo Excelu.</p>
                </div>
                <div className="flex gap-2 items-center">
                    <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="report-year"
                            className="border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-orange-600">
                        {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month"
                            className="border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-orange-600">
                        <option value="">Celý rok</option>
                        {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                    <button onClick={() => exportFile("pdf")} data-testid="report-export-pdf"
                            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                        <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button onClick={() => exportFile("xlsx")} data-testid="report-export-xlsx"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                        <Download className="w-3.5 h-3.5" /> Excel
                    </button>
                </div>
            </div>

            {!data ? (
                <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání…</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <Kpi label="Fakturováno" value={fmt(data.invoiced)} sub={`${data.invoices_count} faktur`} testid="kpi-invoiced" />
                        <Kpi label="Zaplaceno" value={fmt(data.paid)} green testid="kpi-paid" />
                        <Kpi label="Neuhrazeno" value={fmt(data.unpaid)} testid="kpi-unpaid" />
                        <Kpi label="Po splatnosti" value={fmt(data.overdue)} danger={data.overdue > 0} testid="kpi-overdue" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Kpi label="Očekávaný zisk (podle plánu)" value={fmt(data.jobs_total_expected_profit)} green={data.jobs_total_expected_profit >= 0} danger={data.jobs_total_expected_profit < 0} testid="kpi-profit" sub={`skutečný ${fmt(data.jobs_total_actual_profit)}`} />
                        <Kpi label="Náklady zakázek" value={fmt(data.jobs_total_costs)} />
                        <Kpi label="Řemeslníci" value={fmt(data.craftsmen_total)} />
                        <Kpi label="Kniha jízd" value={fmt(data.trips_amount)} sub={`${data.trips_km} km`} />
                    </div>

                    <AccountantPackage year={year} month={month} />

                    <div className="bg-white border border-slate-200">
                        <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-3">
                            <BarChart3 className="w-5 h-5 text-orange-600" />
                            <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Zisk podle zakázek ({data.jobs_count})</div>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                    <th className="px-6 py-3 font-semibold">Zakázka</th>
                                    <th className="px-6 py-3 font-semibold">Klient</th>
                                    <th className="px-6 py-3 font-semibold">Typ</th>
                                    <th className="px-6 py-3 font-semibold text-right">Výnos</th>
                                    <th className="px-6 py-3 font-semibold text-right">Náklady</th>
                                    <th className="px-6 py-3 font-semibold text-right">Očekávaný zisk<br /><span className="normal-case tracking-normal font-normal">podle plánu</span></th>
                                    <th className="px-6 py-3 font-semibold text-right">Skutečný zisk<br /><span className="normal-case tracking-normal font-normal">vyfakturováno − náklady</span></th>
                                </tr>
                            </thead>
                            <tbody className="tabular-data">
                                {data.jobs.length === 0 && (
                                    <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400">Žádné zakázky v období.</td></tr>
                                )}
                                {data.jobs.map((j, i) => (
                                    <tr key={j.job_id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`} data-testid={`report-job-${j.job_id}`}>
                                        <td className="px-6 py-3"><Link to={`/zakazky/${j.job_id}`} className="font-medium text-slate-900 hover:text-orange-600">{j.title}</Link></td>
                                        <td className="px-6 py-3 text-slate-600">{j.client_name || "—"}</td>
                                        <td className="px-6 py-3"><PayModeBadge mode={j.pay_mode} testid={`report-pay-mode-${j.job_id}`} /></td>
                                        <td className="px-6 py-3 text-right">{fmt(j.revenue)}</td>
                                        <td className="px-6 py-3 text-right">{fmt(j.costs)}</td>
                                        <td className={`px-6 py-3 text-right font-semibold ${j.expected_profit == null ? "text-slate-300" : j.expected_profit >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`report-expected-${j.job_id}`}>{j.expected_profit == null ? "—" : fmt(j.expected_profit)}</td>
                                        <td className={`px-6 py-3 text-right font-bold ${j.actual_profit >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`report-actual-${j.job_id}`}>{fmt(j.actual_profit)}<div className="text-[10px] font-normal text-slate-400">fakturováno {fmt(j.invoiced_subtotal)}</div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            <MaterialConsumptionReport year={year} />
        </div>
    );
}

function Kpi({ label, value, sub, green, danger, testid }) {
    return (
        <div data-testid={testid} className={`bg-white border p-5 sharp-shadow ${danger ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}>
            <div className="label-eyebrow mb-2">{label}</div>
            <div className={`font-display text-xl font-black tabular-data ${green ? "text-emerald-700" : danger ? "text-red-700" : "text-slate-900"}`}>{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}
